import React, { useState, useRef, useEffect, useMemo } from 'react';
import delve from 'dlv';
import useSearchOnItems from 'hooks/useSearchOnItems';
import MenuRoot from '../index';
import SearchField from 'components/styled/SearchField/SearchField';
import { ItemWrapper } from '../Menu';
import Box from 'components/elements/Box';
import colors from 'styles/themes/colors';
import useListKeyboardNavigation from '../useKeyboardNavigation';
import sortByDisplayName from 'helpers/sortByDisplayName';
import {
  DisabledMessage,
  Figure,
  HelperText,
  ISearchableMenuProps,
  LayoutWrapper,
  ListsScrollableWrapper,
  Line,
} from '../SearchableMenu';
import { IRegionProps, TRegionType } from 'types/shared';
import pluralize from 'helpers/pluralize';
import { groupArrayBy } from 'helpers/groupArrayBy';
import { CUSTOM_USER_REGION_TYPE } from 'config';

interface IRegionMenuProps extends ISearchableMenuProps {
  myItems?: IRegionProps[];
}

const otherRegionType: TRegionType[] = [
  'Affiliate Region',
  'Custom Region',
  'Emerging Markets Region',
];

const RegionSearchableMenu: React.FC<IRegionMenuProps> = (props) => {
  const {
    items,
    myItems = [],
    selectedItem,
    selectedItems,
    multipleSelections,
    Item,
    ListLabel,
    itemToString,
    getMenuProps,
    getItemProps,
    overflowLeft,
    entityColorScale,
    additionalFindMethod,
    children,
    highlightedIndex,
    setHighlightedIndex,
    selectHighlightedItem,
    isDisplayedWithFlag,
    showListLabel = false,
    showAllItems = true,
    disabledMessage,
    disabled,
    isSmallScreen,
    isMultipleSelection,
    textToSelect,
  } = props;
  const [searchValue, setSearchValue] = useState<string>('');
  const searchRef = useRef<HTMLInputElement>(null);
  const allRegionList = useMemo(() => {
    return multipleSelections ? [...multipleSelections, ...items] : items;
  }, [items, multipleSelections]);
  const filteredItems = useSearchOnItems(
    allRegionList,
    searchValue,
    additionalFindMethod
  );
  const filteredMyItems = useSearchOnItems(
    myItems,
    searchValue,
    additionalFindMethod
  );

  const isLengthZero = (item: any): boolean =>
    delve(item, 'children', item).length === 0;
  const noResults = [...filteredItems, ...filteredMyItems].every(isLengthZero);

  useListKeyboardNavigation(
    filteredItems.length,
    highlightedIndex,
    setHighlightedIndex,
    selectHighlightedItem
  );
  useEffect(() => {
    searchRef.current?.focus();
  }, []);
  const handleSearch = (evt: any): void => {
    setHighlightedIndex(0);
    setSearchValue(evt.target.value);
  };

  const itemsToShow = useMemo(() => {
    let originalItems = items;
    if (!isMultipleSelection || searchValue) {
      originalItems = items.filter((item: IRegionProps) =>
        additionalFindMethod(item, searchValue)
      );
    }

    // we should combine 3 region types into 'Other Regions' category. So we just replace their regionType to Other Regions
    const mappedItems = originalItems.map((item: IRegionProps) => ({
      ...item,
      regionType: otherRegionType.includes(item.regionType)
        ? 'Other Regions'
        : item.regionType,
    }));

    return groupArrayBy(mappedItems, 'regionType');
  }, [additionalFindMethod, items, searchValue, isMultipleSelection]);

  return (
    <LayoutWrapper overflowLeft={overflowLeft}>
      {!disabled ? (
        <>
          <Box borderBottom={`1px solid ${colors.LightGrey}`}>
            <SearchField
              refObj={searchRef}
              handleSearch={handleSearch}
              value={searchValue}
              placeholder="Search"
            />
          </Box>
          {!noResults && (
            <ListsScrollableWrapper
              {...getMenuProps({ 'data-menu-list': true })}
            >
              {ListLabel && showListLabel && (
                <li>
                  <Figure data-menu-figure={true}>
                    {ListLabel && filteredItems.length > 0 && (
                      <figcaption>
                        <ListLabel {...props} />
                      </figcaption>
                    )}
                  </Figure>
                </li>
              )}

              {/* SELECTED SECTION */}
              {isMultipleSelection &&
                !!multipleSelections?.length &&
                !searchValue && (
                  <>
                    <li>
                      <Figure data-menu-figure={true}>
                        {ListLabel && (
                          <figcaption>
                            <ListLabel>SELECTED</ListLabel>
                          </figcaption>
                        )}
                      </Figure>
                    </li>
                    {multipleSelections.map(
                      (item: IRegionProps, index: number) => (
                        <ItemWrapper
                          key={itemToString(item)}
                          {...getItemProps({
                            'data-menu-list-item': true,
                            key: itemToString(item),
                            item: item,
                            isActive: true,
                          })}
                        >
                          <Item
                            item={item}
                            index={index}
                            isHovered={highlightedIndex === index}
                            isActive={
                              itemToString(selectedItem) ===
                                itemToString(item) ||
                              (selectedItems &&
                                selectedItems.includes(itemToString(item)))
                            }
                            entityColorScale={entityColorScale}
                            isDisplayedWithFlag={isDisplayedWithFlag}
                            isSmallScreen={isSmallScreen}
                            customRegionId={
                              item.regionType === CUSTOM_USER_REGION_TYPE
                                ? item.id
                                : undefined
                            }
                          />
                        </ItemWrapper>
                      )
                    )}
                    <li>
                      <Figure data-menu-figure={true}>
                        <Line />
                      </Figure>
                    </li>
                  </>
                )}

              {/* MY REGIONS SECTION */}
              {!!filteredMyItems && filteredMyItems.length > 0 && (
                <>
                  <li>
                    <Figure data-menu-figure={true}>
                      {ListLabel && filteredMyItems.length > 0 && (
                        <figcaption>
                          <ListLabel {...props}>{`MY ${pluralize(
                            textToSelect?.toLocaleUpperCase(),
                            2
                          )}`}</ListLabel>
                        </figcaption>
                      )}
                    </Figure>
                  </li>
                  <li key={`root-my-item-list`}>
                    <ul>
                      {filteredMyItems.map((item, index) => (
                        <ItemWrapper
                          key={`my-list-${itemToString(item)}`}
                          {...getItemProps({
                            'data-menu-list-item': true,
                            key: itemToString(item),
                            item: item,
                            isActive: index === highlightedIndex,
                          })}
                        >
                          <Item
                            item={item}
                            index={index}
                            isHovered={
                              index + multipleSelections.length ===
                              highlightedIndex
                            }
                            isActive={
                              isMultipleSelection
                                ? selectedItems &&
                                  selectedItems.includes(itemToString(item))
                                : itemToString(selectedItem) ===
                                  itemToString(item)
                            }
                            entityColorScale={entityColorScale}
                            isDisplayedWithFlag={isDisplayedWithFlag}
                            isSmallScreen={isSmallScreen}
                            customRegionId={item.id}
                          />
                        </ItemWrapper>
                      ))}
                    </ul>
                  </li>
                  <li>
                    <Figure data-menu-figure={true}>
                      <Line />
                    </Figure>
                  </li>
                </>
              )}

              {/* BUSINESS REGIONS SECTION */}
              {(showAllItems || searchValue) &&
                Object.keys(itemsToShow).map((key) => {
                  const displayItems = itemsToShow[key];
                  return displayItems.length > 0 ? (
                    <li key={key}>
                      <ul>
                        <li>
                          <Figure data-menu-figure={true}>
                            {displayItems.length > 0 && (
                              <figcaption>
                                <ListLabel {...props}>
                                  {key.toLocaleUpperCase()}
                                </ListLabel>
                              </figcaption>
                            )}
                          </Figure>
                        </li>
                        <li key={`root-business-list`}>
                          <ul>
                            {displayItems
                              .sort(sortByDisplayName)
                              .map((item: any, index: any): any => (
                                <ItemWrapper
                                  key={`${itemToString(item)}_${index}`}
                                  {...getItemProps({
                                    'data-menu-list-item': true,
                                    key: `${itemToString(item)}_${index}_props`,
                                    item: item,
                                    isActive:
                                      index + items.length === highlightedIndex,
                                  })}
                                >
                                  <Item
                                    item={item}
                                    index={index + items.length}
                                    isHovered={
                                      index +
                                        items.length +
                                        multipleSelections?.length +
                                        myItems.length ===
                                      highlightedIndex
                                    }
                                    isActive={
                                      isMultipleSelection
                                        ? selectedItems &&
                                          selectedItems.includes(
                                            itemToString(item)
                                          )
                                        : itemToString(selectedItem) ===
                                          itemToString(item)
                                    }
                                    entityColorScale={entityColorScale}
                                    isDisplayedWithFlag={isDisplayedWithFlag}
                                    isSmallScreen={isSmallScreen}
                                  />
                                </ItemWrapper>
                              ))}
                          </ul>
                        </li>
                      </ul>
                    </li>
                  ) : null;
                })}
            </ListsScrollableWrapper>
          )}
          {noResults && (
            <HelperText>
              Didn&apos;t find what you&apos;re looking for? <br /> Try again
              with a different search term or try a different spelling.
            </HelperText>
          )}
          {children}
        </>
      ) : (
        disabledMessage && <DisabledMessage>{disabledMessage}</DisabledMessage>
      )}
    </LayoutWrapper>
  );
};
const Menu: React.FC<any> = (props) => {
  return (
    <MenuRoot {...props}>
      {(renderProps): any => (
        <RegionSearchableMenu {...props} {...renderProps}>
          {props.children}
        </RegionSearchableMenu>
      )}
    </MenuRoot>
  );
};
export default Menu;
